import { Component, input, output } from '@angular/core';
import { LucideDynamicIcon, LucidePanelLeftClose, LucidePanelLeftOpen, provideLucideIcons } from '@lucide/angular';
import { UiSideNavBrandComponent } from '../side-nav-brand/ui-side-nav-brand.component';
import { UiSideNavComponent } from '../side-nav/ui-side-nav.component';

@Component({
  selector: 'ui-sidebar',
  standalone: true,
  imports: [LucideDynamicIcon, UiSideNavBrandComponent, UiSideNavComponent],
  providers: [provideLucideIcons(LucidePanelLeftClose, LucidePanelLeftOpen)],
  host: {
    '[class.ui-sidebar-host--drawer]': 'drawer()',
  },
  template: `
    <aside class="ui-sidebar" [class.ui-sidebar--drawer]="drawer()" [class.ui-sidebar--collapsed]="collapsed() && !drawer()">
      <ui-side-nav-brand
        [name]="name()"
        [logoUrl]="logoUrl()"
        [loading]="loading()"
        [tagline]="tagline()"
        [drawer]="drawer()"
        [collapsed]="collapsed() && !drawer()"
        (navigated)="itemActivated.emit()"
      />
      <ui-side-nav
        [drawer]="drawer()"
        [collapsed]="collapsed() && !drawer()"
        [currentUrl]="currentUrl()"
        (itemActivated)="itemActivated.emit()"
      />
      @if (showFooter()) {
        <div class="ui-sidebar__footer">
          @if (!collapsed() || drawer()) {
            <span class="ui-sidebar__footer-label">{{ footerLabel() }}</span>
            <span class="ui-sidebar__footer-version">v{{ version() || '—' }}</span>
          }
          @if (!drawer()) {
            <button
              type="button"
              class="ui-sidebar__collapse-btn"
              [class.ui-sidebar__collapse-btn--solo]="collapsed()"
              [attr.aria-label]="collapsed() ? 'Expandir menu' : 'Recolher menu'"
              (click)="collapseToggle.emit()"
            >
              <svg [lucideIcon]="collapsed() ? 'panel-left-open' : 'panel-left-close'" [size]="18"></svg>
            </button>
          }
        </div>
      }
    </aside>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    :host(:not(.ui-sidebar-host--drawer)) {
      height: 100%;
      overflow: hidden;
    }

    .ui-sidebar {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      width: 17.5rem;
      min-height: 0;
      background: linear-gradient(
        180deg,
        var(--surface-bg, #fff) 0%,
        color-mix(in srgb, var(--surface-muted) 35%, var(--surface-bg, #fff)) 100%
      );
      border-right: 1px solid var(--border-color);
      transition: width 0.2s ease;
    }

    .ui-sidebar--collapsed {
      width: 4.5rem;
    }

    .ui-sidebar:not(.ui-sidebar--drawer) {
      overflow: hidden;
    }

    .ui-sidebar--drawer {
      width: 100%;
      min-height: 0;
      border-right: none;
    }

    .ui-sidebar__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      flex-shrink: 0;
      padding: 0.75rem 1rem;
      border-top: 1px solid var(--border-color);
      background: color-mix(in srgb, var(--surface-muted) 50%, var(--surface-bg, #fff));
    }

    .ui-sidebar--collapsed .ui-sidebar__footer {
      justify-content: center;
      padding-inline: 0.5rem;
    }

    .ui-sidebar__collapse-btn--solo {
      margin-inline: auto;
      margin-left: auto;
    }

    .ui-sidebar__footer-label {
      font-size: 0.6875rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--text-muted, #94a3b8);
    }

    .ui-sidebar__footer-version {
      font-size: 0.6875rem;
      color: var(--text-muted, #94a3b8);
    }

    .ui-sidebar__collapse-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      flex-shrink: 0;
      margin-left: auto;
      border: 1px solid var(--border-color);
      border-radius: 0.5rem;
      background: var(--surface-bg, #fff);
      color: var(--text-muted, #64748b);
      cursor: pointer;
      transition: background 0.15s, color 0.15s, border-color 0.15s;
    }

    .ui-sidebar__collapse-btn:hover {
      color: var(--brand-primary, #1677ff);
      border-color: color-mix(in srgb, var(--brand-primary) 35%, var(--border-color));
      background: color-mix(in srgb, var(--brand-primary) 6%, var(--surface-bg, #fff));
    }
  `],
})
export class UiSidebarComponent {
  name = input('');
  logoUrl = input<string | null>(null);
  loading = input(false);
  tagline = input('Painel de gestão');
  currentUrl = input('');
  version = input('');
  footerLabel = input('Plataforma Veitros');
  drawer = input(false);
  showFooter = input(true);
  collapsed = input(false);

  itemActivated = output<void>();
  collapseToggle = output<void>();
}
